import Vue from 'vue'
import VueAxios from 'vue-axios'
import axios from 'axios'
import VueAuthorize from '@swoop-ltd/vue-authorize'
import VueInstant from '@swoop-ltd/vue-instant'
import { ComponentLibrary } from '@swoop-ltd/embeddable-forms-vue'
import VModal from 'vue-js-modal'
import Notifications from 'vue-notification'
import Vuebar from 'vuebar'
import VShowSlide from 'v-show-slide'
import { VPopover } from 'v-tooltip'
import Toasted from 'vue-toasted'
import ToastPlugin from '@/plugins/toast'
import { Buffer } from 'buffer'
import router from './router'
import initRoutes from './router/routes'
import App from './App.vue'
import store from './store'
import Icon from './config/vue-awesome'
import roles from './config/roles'
import permissions from './config/permissions'
import initVeeValidations from './config/vee-validate.validations'
import Meta from 'vue-meta'
import { i18n } from '@/config/vue-i18n'
import registerComponents from './components'
import registerDirectives from './directives'
import registerFilters from './filters'
import { initSentry } from '@/config/sentry'

import './assets/styles/index.scss'
import GenericButton from '@/components/GenericButton.vue'
import { initWhitelabel } from '@/composables/useWhitelabel'
import { loadExternal } from '@/utils/3rdPartyScripts'
import { applyRequestHeadersToAllInstances } from '@/api'
import { countryNameFromCode } from '@/utils/countries'
import { mapCountryCodeToTenant } from '@/utils/tenant'
import initCreditPassportModule from '@/features/creditPassport'
import { defaultGroupId } from '@/utils/constants'
import { authGetters, groupRoutine } from '@/store/modules/auth/routines'
import PageNotFound from '@/components/PageNotFound.vue'
import { determineEnvironment } from '@/utils/utils'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import PortalVue from 'portal-vue'
import { registerChunkReload, clearChunkReloadFlag } from '@/utils/chunkReload'

// Enable Buffer polyfill
// https://github.com/vitejs/vite/discussions/2785
window.Buffer = Buffer

registerChunkReload()

// Setup vue config
Vue.config.productionTip = false

// Setup vue middlewares
Vue.use(VueAxios, axios)
Vue.use(VueAuthorize, {
  roles,
  permissions
})
Vue.use(VueInstant)
Vue.use(VModal)
Vue.use(Notifications)

Vue.component('GenericButton', GenericButton)
Vue.use(Vuebar)
Vue.use(VShowSlide)
Vue.use(Toasted)
Vue.use(ToastPlugin)
Vue.use(Meta)

Vue.use(VueDOMPurifyHTML)
Vue.use(PortalVue)

Vue.use(ComponentLibrary)

initSentry()

// Init vee-validate validations
initVeeValidations()

// Declare global custom components
registerComponents(Vue)
Vue.component('icon', Icon)
Vue.component('popover', VPopover)
// Declare global directives
registerDirectives(Vue)

// Declare global filters
registerFilters(Vue)

async function setupApp({ global: globalConfig, portal: portalConfig }) {
  const { enableHubspot, tenantId, countryCode, groupId, clientName } = globalConfig
  const { enableCreditHealthCheck, enableCreditScorePage } = portalConfig
  const groupIdInUrl = new URLSearchParams(window.location.search).get('groupId')
  const isLoggedIn = store.getters[authGetters.IS_AUTHENTICATED]
  const groupIdInStore = store.getters[authGetters.GROUP_ID]
  const groupIdToUse = groupIdInUrl ?? (isLoggedIn ? groupIdInStore : groupId)
  const user = store.getters[authGetters.USER]

  // Enable Tealium tracking if it's comparethemarket
  if (tenantId.toLowerCase().replace(/\s/g, '') === 'comparethemarket') {
    // Keep this commented out as we need later
    // loadExternal({
    //   src: 'https://tags.tiqcdn.com/utag/ctm/business-finance-v3/qa/utag.sync.js',
    //   async: true
    // })
    const env = determineEnvironment()
    loadExternal({
      src: `https://tags.tiqcdn.com/utag/ctm/business-finance-v3/${env}/utag.js`,
      async: true
    })
  }

  // Enable hubspot tracking if it's enabled in the whitelabel config
  if (enableHubspot) {
    loadExternal({ src: 'https://js-eu1.hs-scripts.com/25104510.js' })
  }

  // Inject `X-Tenant` and `X-Country` headers to all requests based on the country code in the whitelabel config
  applyRequestHeadersToAllInstances({
    'X-Tenant': tenantId,
    'X-Country': countryNameFromCode(countryCode)
  })

  // Initiate whitelabel-dependent modules
  initCreditPassportModule(enableCreditHealthCheck, enableCreditScorePage)

  // Set Group to use within the app
  await store.dispatch(groupRoutine.TRIGGER, {
    id: groupIdToUse,
    name: clientName,
    isExternalGroup: groupIdToUse !== defaultGroupId,
    inviteSource: groupIdInUrl || groupIdInStore ? window.location.href : undefined,
    fromInvite: !!groupIdInUrl || !!user?.fromInvite
  })
}

function injectStyle(href) {
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

function createApp() {
  return new Vue({
    i18n,
    el: '#swoop-root',
    router,
    store,
    render: h => h(App),
    mounted() {
      document.dispatchEvent(new Event('render-event'))
    },
    created() {
      // Reset stage store module on page load/refresh
      this.$store.dispatch('stage/resetStage')
    }
  })
}

function createPageNotFound() {
  return new Vue({
    el: '#swoop-root',
    render: h => h(PageNotFound)
  })
}

;(async () => {
  try {
    const { configData, dictionaryData, styleSheetUrl } = await initWhitelabel()

    await setupApp(configData)

    // The Credit Score routes are in a separate folder. To ensure they work correctly, the app must be fully set up before initializing the routes.
    // If initRoutes is run before the app setup, the pages will display as empty.
    initRoutes()

    injectStyle(styleSheetUrl)
    i18n.mergeLocaleMessage(i18n.locale, dictionaryData)

    createApp()

    clearChunkReloadFlag()
  } catch (e) {
    console.error(e)
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 404) {
        createPageNotFound()
      }
    }
  }
})()
